import { sortBooks } from '../../bookSort.js';

// Which shelves a bookshelf has, what they are called, and the one rule for
// grouping books into them.
//
// Every theme groups through here. Two of them working out the shelves
// separately would eventually disagree about which books are on which, and a
// display switched between themes would appear to gain and lose books.

export const SHELF_ORDER = ['currently-reading', 'to-read', 'read', 'did-not-finish'];

export const SHELF_LABELS = {
  'currently-reading': 'Currently Reading',
  'to-read': 'To Read',
  read: 'Read',
  'did-not-finish': 'Did Not Finish',
};

// A shelf Goodreads did not name is a shelf the reader did, and it arrives as
// the slug they typed: `nyt-best-of-21st-century`. Printed as it stands, on a
// bookcase sign or across a jacket, that reads as a database key that got out.
// The hyphens are word breaks, so this puts them back and capitalises what
// they separate - which is as far as it can honestly go. A word that is really
// an initialism comes out as `Nyt`, because nothing here knows it is one.
const humanShelf = (shelfKey) =>
  shelfKey
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const shelfLabel = (shelfKey) =>
  SHELF_LABELS[shelfKey] || (shelfKey ? humanShelf(shelfKey) : '');

// The shelf a book is filed under, as a key. One function so a book and the
// preload's count of its shelf can never be filed differently.
const shelfKeyOf = (shelf) => shelf || 'other';

// How many books each shelf will have, keyed the way groupByShelf keys them -
// the preload's groups, with a book on no shelf filed under 'other'.
export function expectedShelves(groups) {
  if (!groups) return null;
  const out = {};
  for (const [shelf, count] of Object.entries(groups)) {
    const key = shelfKeyOf(shelf);
    out[key] = (out[key] ?? 0) + count;
  }
  return out;
}

// Shelves come out in reading order, and the books within each in the order
// display settings ask for. Both panels of a theme group through here so the
// shelf and whatever pages through it always agree.
//
// `expected`, when given, is the shelves the preload says are coming, as
// {shelf: count} with "" for books on no shelf. A shelf in it that no book has
// arrived for yet is still listed, empty, so a display drawing its shelves
// before the books can put every one where it will be.
export function groupByShelf(books, settings = {}, expected = null) {
  const groups = new Map();
  for (const book of books) {
    const key = shelfKeyOf(book.shelf);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(book);
  }
  for (const [shelf, count] of Object.entries(expected ?? {})) {
    const key = shelfKeyOf(shelf);
    if (count > 0 && !groups.has(key)) groups.set(key, []);
  }

  // The four Goodreads has, in reading order, and then the reader's own after
  // them. Those are sorted by name rather than left in the order the books
  // happened to arrive in: a shelf's place in the room would otherwise depend
  // on which book of it came back first, so a display could reorder its own
  // bookcases between one five-minute poll and the next with nothing visible
  // to explain it.
  const orderedKeys = [
    ...SHELF_ORDER.filter((key) => groups.has(key)),
    ...[...groups.keys()].filter((key) => !SHELF_ORDER.includes(key)).sort(),
  ];

  return orderedKeys.map((key) => ({
    shelfKey: key,
    books: sortBooks(groups.get(key), settings.sortOrder, settings.sortDirection),
  }));
}

// Below this, what precedes a colon is too short to stand on its own as a
// title - "Home: Habitat, Range, Niche, Territory" is not a book called
// "Home" - so the subtitle stays.
const MIN_MAIN_TITLE = 12;
// And above this, the whole title has no chance of fitting anyway, so
// dropping the subtitle costs nothing that was being read.
const CROWDED_TITLE = 34;

// shortTitle prints a title the way a publisher does on a real spine: the main
// title, and a series as its number rather than its full name repeated.
// Goodreads gives both in one string, and it really does hand back "The
// Hitchhiker's Guide to the Galaxy (Hitchhiker's Guide to the Galaxy, #1)".
//
// Shared, because both themes want it for the same reason and would otherwise
// each grow their own. The untouched title stays on the classic highlight view
// and on every accessible label, so nothing is lost.
export function shortTitle(title) {
  if (!title) return '';

  let text = title;
  const series = text.match(/\s*\(([^()]*?),\s*#([\d.]+)\)\s*$/);
  if (series) {
    text = `${text.slice(0, series.index).trim()} (#${series[2]})`;
  }

  const colon = text.indexOf(': ');
  if (colon >= MIN_MAIN_TITLE && text.length > CROWDED_TITLE) {
    text = text.slice(0, colon);
  }

  return text.trim();
}
